/* eslint-disable new-cap */
import 'extensions/facet-rangeslider';
import 'extensions/checkbox-rangeslider';
import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * Facets
 *--------------------------------------------------------------------------
 *
 * This class is used to style and extend Miva facets functionality.
 *
 */
export default class Facets {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Root container element
	 *
	 * @var Element
	 */
	containerElement;

	/**
	 * Show More functionality elements
	 *
	 * @var Element
	 */
	showMoreElements;

	/**
	 * Show More functionality options
	 *
	 * @var Object
	 */
	showMoreOptions;

	/**
	 * Create a new Facets object
	 *
	 * @param	Object		containerElement						Root container element, all facet elements should be contained within this element.
	 * @param	ElementList	options.appliedFacetElements			List of applied facet elements.
	 * @param	Object		options.showMore						Object containing properties for Show More functionality.
	 * @param	String		options.showMore.countAttribute			Attribute name for Show More facets count. Defaults to 'data-facet-show-count'.
	 * @param	Integer		options.showMore.defaultCount			Default Show More count. Defaults to 5.
	 * @param	ElementList	options.showMore.elements				Show More elements. Defaults to '.g-facet-set__list-show-more'.
	 * @param	String		options.showMore.lessTextAttribute		Attribute name for Show Less text. Defaults to 'data-facet-show-less-text'.
	 * @param	String		options.showMore.linkElementSelector	Selector for Show More link element. Defaults to '.g-facet-set__list-show-more-link'.
	 * @param	String		options.showMore.moreTextAttribute		Attribute name for Show More text. Defaults to 'data-facet-show-more-text'.
	 */
	constructor (containerElement, {
		appliedFacetElements = '.js-facets__applied-label',
		showMore = {
			countAttribute: 'data-facet-show-count',
			defaultCount: 5,
			elements: '.g-facet-set__list-show-more',
			lessTextAttribute: 'data-facet-show-less-text',
			linkElementSelector: '.g-facet-set__list-show-more-link',
			moreTextAttribute: 'data-facet-show-more-text'
		}
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.containerElement = this.#validator.isElement('containerElement', containerElement);

		this.appliedFacetElements = this.#validator.isElementList('appliedFacetElements', appliedFacetElements);

		this.showMoreOptions = this.#validator.isObject('showMore', showMore);
		this.#validator.isString('showMore.countAttribute', this.showMoreOptions.countAttribute);
		this.#validator.isInt('showMore.defaultCount', this.showMoreOptions.defaultCount);
		this.#validator.isString('showMore.lessTextAttribute', this.showMoreOptions.lessTextAttribute);
		this.#validator.isString('showMore.linkElementSelector', this.showMoreOptions.linkElementSelector);
		this.#validator.isString('showMore.moreTextAttribute', this.showMoreOptions.moreTextAttribute);

		this.showMoreElements = this.#validator.isElementList('showMore.elements', this.showMoreOptions.elements, {
			container: this.containerElement,
			required: false
		});

		this.bindEvents();
	}

	bindEvents () {
		// Bind the facet set accordions (collapse/uncollapse)
		this.bindFacetSetAccordions();

		// Bind the Show More/Show Less events
		this.bindShowMoreEvents();

		// Bind the range slider events
		this.bindRangeSliders();

		// Bind the media query events (Toggle facet sets display on mobile)
		this.bindMediaQueryEvents();

		// Bind Clear Single Attribute events
		this.bindAppliedFacetEvents();
	}

	bindFacetSetAccordions () {
		const facetSets = this.containerElement.getElementsByClassName('g-facet-set');

		if (!facetSets.length) {
			return;
		}

		const collapsedClassName = 'g-facet-set--collapsed';

		for (const set of facetSets) {
			const setList = set.querySelector('.g-facet-set__list');
			const setLabel = set.querySelector('.g-facet-set__label');

			if (!setList || !setLabel) {
				return;
			}

			setLabel.addEventListener('click', (event) => {
				event.preventDefault();

				if (set.classList.contains(collapsedClassName)) {
					set.classList.remove(collapsedClassName);
					setLabel.setAttribute('aria-expanded', 'true');
				} else {
					set.classList.add(collapsedClassName);
					setLabel.setAttribute('aria-expanded', 'false');
				}
			});
		}
	}

	/* eslint-disable max-lines-per-function*/
	bindShowMoreEvents () {
		if (!this.showMoreElements || !this.showMoreElements.length) {
			return;
		}

		const showLessClassName = 'is-show-less';

		for (const element of this.showMoreElements) {
			const showMoreLink = element.querySelector(this.showMoreOptions.linkElementSelector);
			const setList = element.closest('.g-facet-set__list');

			if (!showMoreLink || !setList) {
				return;
			}

			const setListItems = setList.querySelectorAll(`.g-facet-set__list-item:not(${this.showMoreOptions.element})`);

			if (!setListItems.length) {
				return;
			}

			let showCount = parseInt(element.getAttribute(this.showMoreOptions.countAttribute), 10);

			if (!showCount || isNaN(showCount)) {
				showCount = this.showMoreOptions.defaultCount;
			}

			const showMoreText = showMoreLink.getAttribute(this.showMoreOptions.moreTextAttribute) || 'Show More';
			const showLessText = showMoreLink.getAttribute(this.showMoreOptions.lessTextAttribute) || 'Show Less';

			for (let i = 0, len = setListItems.length; i < len; i++) {
				if (i >= showCount) {
					setListItems[i].classList.add('u-hidden');
				}
			}

			showMoreLink.addEventListener('click', (event) => {
				event.preventDefault();

				let isShowLess = false;

				if (showMoreLink.classList.contains(showLessClassName)) {
					showMoreLink.classList.remove(showLessClassName);
				} else {
					showMoreLink.classList.add(showLessClassName);
					isShowLess = true;
				}

				for (let i = 0, len = setListItems.length; i < len; i++) {
					const item = setListItems[i];

					if (i >= showCount && !isShowLess) {
						item.classList.add('u-hidden');
					} else {
						item.classList.remove('u-hidden');
					}
				}

				if (isShowLess) {
					showMoreLink.textContent = showLessText;
				} else {
					showMoreLink.textContent = showMoreText;
				}
			});

			element.classList.remove('u-hidden');
		}
	}
	/* eslint-enable max-lines-per-function*/

	bindRangeSliders () {
		const observer = new MutationObserver((mutations, me) => {
			// Wait until Miva runtime ui JS is loaded
			if (typeof window.MMFacet_RangeSlider_Initialize === 'function') {
				me.disconnect();

				/**
				 * This function will reinitialize the Range Sliders when the page resizes.
				 */
				let animationTimeout = 0;
				const rangeSliders = this.containerElement.querySelectorAll('[data-mm-facet-rangeslider-name]');

				window.addEventListener('resize', () => {
					if (animationTimeout) {
						window.cancelAnimationFrame(animationTimeout);
					}

					animationTimeout = window.requestAnimationFrame(() => {
						if (rangeSliders.length) {
							window.MMFacet_RangeSlider_Initialize();
						}
					});
				}, false);
			}
		});

		observer.observe(document, {
			childList: true,
			subtree: true
		});
	}

	bindMediaQueryEvents () {
		const media = window.matchMedia('(min-width: 60rem)');

		this.mediaChanged(media.matches);

		media.addEventListener('change', (event) => this.mediaChanged(event.matches));
	}

	mediaChanged (matches) {
		if (matches) {
			// Check if the Modal was opened and then screen size got larger.
			if (window.MicroModal && this.containerElement.classList.contains('is-open')) {
				const closeButton = this.containerElement.querySelector('[data-micromodal-close]');

				if (closeButton) {
					closeButton.click();
				}
			}

			this.containerElement.setAttribute('aria-hidden', 'false');

			return;
		}

		this.containerElement.setAttribute('aria-hidden', 'true');
	}

	bindAppliedFacetEvents () {
		for (const appliedFacetElement of this.appliedFacetElements) {
			appliedFacetElement.addEventListener('click', () => this.clearAppliedFacet(appliedFacetElement));
		}
	}

	clearAppliedFacet (element) {
		const form = element.form;

		if (!form || typeof window.MMProdList_UpdateQuery !== 'function') {
			return;
		}

		const facetElement = form.querySelector(`[name="${element.getAttribute('data-facet-code')}"]`);

		if (!facetElement) {
			return;
		}

		if (facetElement.nodeName === 'X-CHECKBOX-RANGESLIDER' || facetElement.nodeName === 'X-FACET-RANGESLIDER') {
			facetElement.clearFacet();

			return;
		}

		if (facetElement.nodeName === 'SELECT') {
			facetElement.value = '';
			window.MMProdList_UpdateQuery(facetElement);

			return;
		}

		const option = form.querySelector(`[name="${element.getAttribute('data-facet-code')}"][value="${element.getAttribute('data-facet-value')}"]`);

		if (!option) {
			return;
		}

		option.checked = false;
		window.MMProdList_UpdateQuery(option);
	}

}
